import React, {useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import StyledAssistantQuery from 'components/organisms/Queries/AssistantQuery/AssistantQuery.styles';
import constants from 'helpers/constants';
import Box from 'components/atoms/Layout/Box/Box';
import {useEntitySearchAnalyse} from 'services/entity/entity.hooks';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Button from 'components/atoms/Buttons/Button/Button';
import ActionButton from 'components/molecules/Buttons/ActionButton/ActionButton';
import dom from 'helpers/dom';
import ListWrapper from 'components/templates/Wrappers/Headers/ListWrapper/ListWrapper';
import DataList from 'components/organisms/Lists/DataList/DataList';
import ConversationCard from 'components/organisms/Cards/ConversationCard/ConversationCard';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import {useSnackbar} from 'components/organisms/Providers/SnackbarProvider/SnackbarProvider';
import Delete from '@mui/icons-material/Delete';
import useMediaQuery from '@mui/material/useMediaQuery';
import ConfirmDialog from 'components/organisms/Dialogs/ConfirmDialog/ConfirmDialog';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import List from 'components/atoms/Lists/List/List';
import ListItem from 'components/atoms/Lists/ListItem/ListItem';
import {useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import Help from '@mui/icons-material/Help';

const AssistantQuery = React.forwardRef((props, ref) => {
  const {
    query,
    state,
    terms,
    wizard,
    examples,
    similarity,
    fieldData,
    isLoading,
    initialised,
    actions,
    count,
    topIds,
    autoStart,
    autoApply,
    navigation,
    onChange,
    onToggle,
    ...innerProps
  } = useComponentProps({
    initialised: true,
    navigation: false,
    autoApply: true,
    ...props
  }, 'AssistantQuery');

  const innerRef = useRef(null);
  const formRef = useRef(null);
  const fieldRef = useRef(null);
  const activeRef = useRef(false);
  const scrollRef = useRef(false);
  const initialisedRef = useRef(false);
  const messageIdRef = useRef(null);
  const analysingRef = useRef(null);

  const [internalState, setInternalState] = useState({
    loadingIdx: 0,
    sendDisabled: true,
    applyingActionIds: []
  });

  const snackbar = useSnackbar();
  const authorize = useAuthorize();
  const dialogControl = useDialogControl();
  const entitySearchAnalyse = useEntitySearchAnalyse();

  const termsMemo = useEffectItem(terms);
  const examplesMemo = useEffectItem(examples);

  const xsDown = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const lsDown = useMediaQuery((theme) => theme.breakpoints.down('ls'));

  const assistantQuery = useMemo(() => ({
    refs: {
      ref: innerRef,
      formRef
    },
    state: {
      ...internalState,
      ...state
    }
  }), [internalState, state]);

  useImperativeHandle(ref, () => assistantQuery);

  const isSearchAssistant = assistantQuery.state.assistantType === constants.assistant.types.search;

  const types = useMemo(() => {
    return constants.data.assistantTypes
      .filter((ea) => {
        return ea.enabled === true || (
          ea.enabled?.length > 0 && ea.enabled.includes('database')
        )
      })
      .filter((ea) => authorize({attribute: `entity.assistant.${ea.value}`}));
  }, [authorize]);

  const wasInitialised = initialised || activeRef.current;
  useLayoutEffect(() => {
    activeRef.current = wasInitialised;
    return () => {
      activeRef.current = false;
    }
  }, [wasInitialised]);

  const hasAssistantQuery = query && query.assistant;
  const activeMessage = useMemo(() => {
    return hasAssistantQuery ? (assistantQuery.state.assistantConversation ?? []).findLast((c) => c.role === constants.assistant.conversation.roles.user) : null;
  }, [hasAssistantQuery, assistantQuery.state.assistantConversation]);

  const [conversationMemo, loadingTitles, lastIndex] = useMemo(() => {
    if (hasAssistantQuery) {
      const conversationId = activeMessage?.messageId ?? Date.now();

      let conversation = utils.clone(assistantQuery.state.assistantConversation ?? [], true);

      let loadingTitles = [`Analysing instructions`];
      let extraTitles = [];
      activeMessage?.types?.forEach((type) => {
        if (![constants.assistant.types.setup, constants.assistant.types.other].includes(type)) {
          const assistantType = constants.data.lookup('assistantTypes', type);

          if (assistantType) {
            extraTitles = extraTitles.concat([
              `Analysing ${assistantType.loader}`,
              `Finding ${assistantType.loader}`,
              `Matching ${assistantType.loader}`,
              `Verifying ${assistantType.loader}`,
              `Formatting ${assistantType.loader}`
            ]);
          }
        }
      })
      if (extraTitles.length > 0) {
        loadingTitles = [...loadingTitles, ...extraTitles];
      } else {
        loadingTitles = loadingTitles.concat([
          'Finding query',
          'Finding actions',
          'Matching query',
          'Matching actions',
          'Verifying query',
          'Verifying actions',
          'Formatting query',
          'Formatting actions'
        ]);
      }

      const welcome = '**Welcome to the search assistant**  \nStart by describing the type of company you are looking for. This assistant can also help you research companies with AI, based on publicly available information.';

      const lastTotal = (isSearchAssistant ? conversation.findLast((c) => {
        return c.role === constants.assistant.conversation.roles.user && utils.isDefined(c.progress?.count);
      })?.progress?.count : null) ?? count;

      conversation = [
        ...(autoStart ? [] : [{
          messageId: utils.sha1(`${0}_${conversationId}`),
          role: constants.assistant.conversation.roles.system,
          message: welcome
        }]),
        ...conversation
      ];

      let first = true, firstSearch = true, lastIndex = 0,
        activeIdx = conversation.findLastIndex((c) => c.role === constants.assistant.conversation.roles.user);

      conversation = conversation.reduce((a, c, idx) => {
        let isSearch = c.types?.includes(constants.assistant.types.search) &&
          !c.types?.includes(constants.assistant.types.setup);

        c.active = idx === activeIdx;

        if (!c.auto) {
          let message = c.message;
          if (utils.isObject(c.message) || utils.isArray(c.message)) {
            message = '';
            utils.toArray(c.message).forEach((m) => {
              if (message.length > 0) {
                message += '  \n*&nbsp;*  \n';
              }
              message += `**${m.question.trim()}**`;

              let answer = utils.toArray(m.answer).join(', ').trim();
              if (answer.length > 0) {
                message += `  \n${answer}`;
              }
            });
          }

          a.push({
            ...c,
            message
          });
        }

        if (c.role === constants.assistant.conversation.roles.user && !c.placeholder) {
          const needMoreAction = c.results?.actions?.find((action) => {
            return [
              constants.assistant.adviceActionTypes.askQuestion,
              constants.assistant.adviceActionTypes.needMoreInfo
            ].includes(action.id)
          });

          const actions = c.results?.actions?.filter((action) => action.id !== needMoreAction?.id);

          let message = '', info;
          if (first) {
            if (autoStart) {
              message += welcome;
            } else {
              if (isSearch) {
                message += 'Thank you for your instruction, a new assistant column will appear in the table and the company profile. The assistant information will show an explanation why the company has been chosen as good fit.';
                firstSearch = false;
              } else {
                message += `Thank you for your instruction, the assistant will now start analysing your instruction and generate a list of actions.`;
              }
            }
          } else if (firstSearch) {
            if (isSearch) {
              message += 'Thank you for your instruction, a new assistant column will appear in the table and the company profile. The assistant information will show an explanation why the company has been chosen as good fit.';
              firstSearch = false;
            }
          }

          if (message.length > 0) {
            message += ' ';
          }
          if (isSearch) {
            let messageTotal = c.active ? lastTotal : (c.progress?.count ?? 0);
            message += `The assistant will now start processing **${utils.formatNumber(messageTotal ?? 0)}** compan${(messageTotal ?? 0) === 1 ? 'y' : 'ies'}.`;
          } else {
            message += `Please wait while the assistant prepares the next steps for you.`;
          }

          if (c.active) {
            if (isLoading) {
              info = {
                message: 'Loading results...'
              };
            } else {
              if (+(c.progress?.part ?? 0) !== 100) {
                if (assistantQuery.state.blocked) {
                  info = {
                    color: 'error',
                    message: `${assistantQuery.state.error}.`
                  };
                } else {
                  if (isSearch) {
                    let batchSize = Math.min(c.limits.input, ((lastTotal ?? 0) - (c.processed?.input ?? 0)));
                    info = {
                      message: `${(+(c.progress?.total ?? 0) > 0) ? `Found **${utils.formatNumber(c.processed?.output ?? 0)}** compan${(c.processed?.output ?? 0) === 1 ? 'y' : 'ies'} so far. ` : ''}Processing${batchSize < (lastTotal ?? 0) ? (+(c.progress?.total ?? 0) === 0 ? ' first' : ' next') : ''} **${batchSize}** compan${batchSize === 1 ? 'y' : 'ies'}...`
                    };
                  } else {
                    info = {
                      message: `${loadingTitles[assistantQuery.state.loadingIdx]}...`,
                      progress: Math.floor((assistantQuery.state.loadingIdx + 1) / (loadingTitles.length + 1) * 100)
                    };
                  }
                }
              } else {
                if (isSearch) {
                  if (+(c.progress?.total ?? 0) < 100) {
                    info = {
                      message: `Processed **${utils.formatNumber(c.processed?.input ?? 0)}** compan${(c.processed?.input ?? 0) === 1 ? 'y' : 'ies'} and found **${utils.formatNumber(c.processed?.output ?? 0)}** compan${(c.processed?.output ?? 0) === 1 ? 'y' : 'ies'}. You can process more companies by pressing the button below.`
                    };
                  } else if (+(c.progress?.total ?? 0) === 100) {
                    info = {
                      message: `Finished processing and found **${utils.formatNumber(c.processed?.output ?? 0)}** compan${(c.processed?.output ?? 0) === 1 ? 'y' : 'ies'}. You can add more instructions by pressing the button below.`
                    };
                  }
                } else {
                  info = {
                    message: `Found **${utils.formatNumber(c.processed?.output ?? 0)}** action${(c.processed?.output ?? 0) === 1 ? '' : 's'}. You can add more instructions below.`
                  };
                }
              }
            }
          } else {
            info = {
              message: 'A new instruction was added, '
            };

            if (+(c.progress?.total ?? 0) === 0) {
              info.message += 'processing was stopped.';
            } else {
              if (isSearch) {
                if (+(c.progress?.total ?? 0) < 100) {
                  info.message += `processed **${utils.formatNumber(c.processed?.input ?? 0)}** compan${(c.processed?.input ?? 0) === 1 ? 'y' : 'ies'} and found **${c.processed?.output ?? 0}** compan${(c.processed?.output) === 1 ? 'y' : 'ies'}.`
                } else {
                  info.message += `finished processing and found **${utils.formatNumber(c.processed?.output ?? 0)}** compan${(c.processed?.output ?? 0) === 1 ? 'y' : 'ies'}.`
                }
              } else {
                info.message += `**${utils.formatNumber(c.processed?.output ?? 0)}** action${(c.processed?.output ?? 0) === 1 ? '' : 's'} ${(c.processed?.output ?? 0) === 1 ? 'was' : 'where'} suggested.`
              }
            }
          }

          a.push({
            messageId: utils.sha1(`${a.length + 1}_${conversationId}`),
            role: constants.assistant.conversation.roles.system,
            related: c,
            info: info,
            message: message
          });

          if (c.active && initialised) {
            if (actions?.length > 0) {
              a.push({
                messageId: utils.sha1(`${a.length + 1}_${conversationId}`),
                role: constants.assistant.conversation.roles.system,
                actions: actions.map((action) => ({
                  ...action,
                  answer: assistantQuery.state.assistantActions?.find((a) => a.applied && a.actionId === action.actionId)?.answer,
                  applied: Boolean(assistantQuery.state.assistantActions?.find((a) => a.applied && a.actionId === action.actionId) ?? false),
                  applying: Boolean(assistantQuery.state.applyingActionIds.find((id) => id === action.actionId) ?? false)
                }))
              });
            }

            if (needMoreAction) {
              a.push({
                messageId: utils.sha1(`${a.length + 1}_${conversationId}`),
                role: constants.assistant.conversation.roles.system,
                actions: [{
                  ...needMoreAction,
                  answer: assistantQuery.state.assistantActions?.find((a) => a.applied && a.actionId === needMoreAction.actionId)?.answer,
                  applied: Boolean(assistantQuery.state.assistantActions?.find((a) => a.applied && a.actionId === needMoreAction.actionId) ?? false),
                  applying: Boolean(assistantQuery.state.applyingActionIds.find((id) => id === needMoreAction.actionId) ?? false)
                }]
              });
            }
          }

          lastIndex = a.length - (c.active ? Math.max(0, (needMoreAction ? 1 : 0) + (actions?.length > 0 ? 1 : 0) - 1) : 0) - 1;
          lastIndex = `${lastIndex}:${a[lastIndex].messageId}`;

          first = false;
        }

        return a;
      }, []);

      return [conversation, loadingTitles, lastIndex];
    } else {
      return [null, [], 0];
    }
  }, [hasAssistantQuery, activeMessage, isSearchAssistant, autoStart, initialised, isLoading, count,
    assistantQuery.state.assistantConversation, assistantQuery.state.blocked, assistantQuery.state.error,
    assistantQuery.state.loadingIdx, assistantQuery.state.assistantActions, assistantQuery.state.applyingActionIds]);

  const needsRecalculation = useMemo(() => {
    return utils.isDefined(activeMessage) && hasAssistantQuery && (
      !isSearchAssistant || query?.version !== activeMessage.version || +(activeMessage.progress?.total ?? 0) !== 100
    ) ? (activeMessage.auto ? 'auto' : 'user') : false;
  }, [query?.version, activeMessage, hasAssistantQuery, isSearchAssistant]);

  const fields = useMemo(() => {
    const fields = [];

    fields.push({
      name: 'message',
      inlineLabel: 'Instruction',
      placeholder: 'Type \'Find...\' to update the search or \'Research...\' to match companies with AI',
      entity: 'assistant',
      type: constants.formFieldTypes.textarea,
      validation: constants.formFieldValidationTypes.text,
      FormFieldProps: {
        ref: fieldRef,
        minRows: 2,
        variant: 'staticLabel',
        hiddenLabel: true
      }
    });

    return fields;
  }, []);

  const setAssistantConversationEvent = useEffectEvent(assistantQuery.state.setAssistantConversation);
  const doAddMessage = useCallback((conversation, message, types = null, verified = false, placeholder = false, auto = false, apply = true) => {
    conversation.push({
      messageId: utils.sha1(`${conversation.length + 1}_${Date.now()}`),
      message: message,
      auto: auto,
      placeholder: placeholder,
      role: constants.assistant.conversation.roles.user,
      types: types,
      verified: verified,
      version: query?.version,
      credits: 0,
      limits: {
        input: constants.assistant.conversation.batchSizes.input,
        output: constants.assistant.conversation.batchSizes.output
      }
    });

    if (apply) {
      setAssistantConversationEvent?.(conversation);
    }
  }, [setAssistantConversationEvent, query?.version]);

  const hasActiveMessage = utils.isDefined(activeMessage);
  useEffect(() => {
    return utils.observeRetry(() => {
      const idx = lastIndex.toString().split(':')[0];
      const ele = innerRef.current?.querySelector?.(`.AssistantQuery-conversation [data-list-index="${idx}"]`);
      if (ele) {
        if (dom.scrollIntoView(ele, {align: 'top', behavior: scrollRef.current ? 'smooth' : 'instant'})) {
          scrollRef.current = true;
        }
      }
    }, 5);
  }, [lastIndex]);

  useEffect(() => {
    if (assistantQuery.state.loading && (assistantQuery.state.loadingIdx + 1) < loadingTitles?.length) {
      return utils.observeTimeout(() => {
        setInternalState((current) => {
          return utils.updater({loadingIdx: current.loadingIdx + 1}, true)(current);
        });
      }, constants.delay.long);
    }
  }, [loadingTitles, assistantQuery.state.loading, assistantQuery.state.loadingIdx]);

  useEffect(() => {
    if (hasAssistantQuery && !hasActiveMessage) {
      setInternalState(utils.updater({showMessage: true}, true));
    }
  }, [hasAssistantQuery, hasActiveMessage]);

  const fieldEl = fieldRef.current?.ref?.current;
  useEffect(() => {
    if (fieldEl) {
      return utils.observeEvent(fieldEl, 'keydown', (e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          formRef.current?.submit();
          e.preventDefault();
          e.stopPropagation();
        }
      }, { capture: true });
    }
  }, [fieldEl]);

  messageIdRef.current = activeMessage?.messageId;
  useEffect(() => {
    const activeMessageId = activeMessage?.messageId;

    if (!isLoading && activeRef.current) {
      if (query && query.assistant) {
        if (!assistantQuery.state.blocked) {
          if (utils.isDefined(activeMessage) && !activeMessage.placeholder && +(activeMessage.progress?.part ?? 0) !== 100 && analysingRef.current !== activeMessageId) {
            analysingRef.current = activeMessageId;
            setInternalState(utils.updater({loading: true, loadingIdx: 0}, true));

            entitySearchAnalyse.mutation.mutateAsync({
              query: query,
              conversation: assistantQuery.state.assistantConversation,
              allowedTypes: utils.uniqueArray(types.map((t) => t.value).concat([assistantQuery.state.assistantType]).filter((_) => (_))),
              count: count,
              topIds: topIds
            })
              .then(({response}) => {
                if (analysingRef.current === activeMessageId) {
                  analysingRef.current = null;
                }

                if (activeMessageId === messageIdRef.current && activeRef.current) {
                  const conversation = utils.camelcase(utils.clone(response.data?.data, true));

                  const userMessages = conversation.filter((c) => c.role === constants.assistant.conversation.roles.user);
                  const activeMessage = userMessages[userMessages.length - 1];
                  const isSearchAssistant = activeMessage.types?.includes(constants.assistant.types.search) &&
                    !activeMessage?.types?.includes(constants.assistant.types.setup);

                  if ((activeMessage?.progress?.total ?? 0) > 0) {
                    if (isSearchAssistant) {
                      // cleanup older results and auto process more
                      userMessages.forEach((c, idx) => {
                        if (idx !== userMessages.length - 1) {
                          delete c.results;
                        }
                      });
                    } else {
                      // add tag colors and valid count
                      if (activeMessage.results?.actions?.length > 0) {
                        activeMessage.results.actions = activeMessage.results.actions
                          .map((action, actionIdx) => {
                            return {
                              ...action,
                              actionId: `${activeMessage.messageId}_${action.id}_${actionIdx}`
                            };
                          });
                      }

                      // cleanup older results
                      if ((activeMessage?.progress?.total ?? 0) > 0) {
                        userMessages.forEach((c, idx) => {
                          if (idx !== userMessages.length - 1) {
                            delete c.results;
                          }
                        });
                      }
                    }
                  } else {
                    // just message type validation
                  }

                  formRef.current?.submitting?.(false);
                  setAssistantConversationEvent?.(conversation);
                  setInternalState(utils.updater({loading: +(activeMessage.progress?.part ?? 0) !== 100, loadingIdx: 0, error: null}, true));
                }
              })
              .catch((error) => {
                if (analysingRef.current === activeMessageId) {
                  analysingRef.current = null;
                }

                if (activeMessageId === messageIdRef.current && activeRef.current) {
                  formRef.current?.submitting?.(false);

                  if (error?.response?.status === constants.http.status.conflict) {
                    setInternalState(utils.updater({error: '0 credits left in your wallet', loading: false, loadingIdx: 0, blocked: true}, true));
                  } else {
                    setInternalState(utils.updater({error: 'Processing instruction failed', loading: false, loadingIdx: 0, blocked: true}, true));
                  }
                }
              });
          }
        }
      }
    }

    return () => {
      setInternalState(utils.updater({loading: false, loadingIdx: 0}, true));
    };
  }, [query, activeMessage, assistantQuery.state.assistantConversation, types, count, topIds, isLoading,
    entitySearchAnalyse.mutation, assistantQuery.state.blocked, assistantQuery.state.assistantType,
    snackbar, setAssistantConversationEvent, fieldData.tagGroups, innerProps.theme]);

  useEffect(() => {
    if (autoStart && hasAssistantQuery && !needsRecalculation && !(assistantQuery.state.assistantConversation?.length > 0)) {
      initialisedRef.current = true;
      doAddMessage([], '', null, false, false, true);
      setInternalState(utils.updater({blocked: false}, true));
    }
  }, [autoStart, hasAssistantQuery, needsRecalculation, assistantQuery.state.assistantConversation, doAddMessage]);

  const setAssistantTypeEvent = useEffectEvent(assistantQuery.state.setAssistantType);
  useEffect(() => {
    if (activeMessage?.types?.length > 0) {
      const messageIsSearchAssistant = activeMessage?.types?.includes(constants.assistant.types.search) &&
        !activeMessage?.types?.includes(constants.assistant.types.setup);

      if (messageIsSearchAssistant !== isSearchAssistant) {
        setAssistantTypeEvent?.(messageIsSearchAssistant ? constants.assistant.types.search : null);
      }
    }
  }, [activeMessage, isSearchAssistant, setAssistantTypeEvent]);

  useEffect(() => {
    if (utils.isDefined(activeMessage?.navigation)) {
      setInternalState(utils.updater({navigation: activeMessage.navigation}, true));
    } else {
      setInternalState((current) => {
        if (!utils.isDefined(current.navigation) || !hasActiveMessage || activeMessage?.auto) {
          return {
            ...current,
            navigation: constants.data.assistantNavigationTypes
              .filter((nt) => {
                return (!utils.isDefined(types) || types.find((t) => t.value === nt.value)) &&
                  (!utils.isDefined(activeMessage?.types) || !activeMessage?.types.find((t) => t === nt.value)) &&
                  (!activeMessage?.auto || nt.value !== constants.assistant.types.setup)
              })
              .map((nt) => nt.value)
          }
        } else {
          return current;
        }
      });
    }
  }, [hasActiveMessage, activeMessage?.navigation, activeMessage?.types, activeMessage?.auto, types]);

  const handleAction = (action, answer, auto) => {
    const skipped = utils.isString(answer) && answer.toLowerCase().includes('skip');

    let handlingAction = false;

    const setApplying = (applying) => {
      setInternalState((current) => {
        return {
          ...current,
          applyingActionIds: applying ? current.applyingActionIds.concat(action.actionId) :
            current.applyingActionIds.filter((id) => id !== action.actionId)
        }
      });
    }

    if (!skipped) {
      setApplying(true);
    }

    const handleClose = () => {
      if (!handlingAction) {
        setApplying(false);
        dialogControl.hide();
      }
    }

    const handleConfirm = () => {
      dialogControl.hide();
      handlingAction = true;

      const applyAction = (action, answer, skipped) => {
        // placeholder for now
        if (skipped || [
          constants.assistant.adviceActionTypes.askQuestion,
          constants.assistant.adviceActionTypes.needMoreInfo
        ].includes(action.id)) {
          return Promise.resolve('Action applied');
        } else if (action.id === constants.assistant.adviceActionTypes.updateQuery) {
          return utils.asPromise(onChange)({...action.details, version: (query?.version ?? 0) + 1}, termsMemo, wizard, examplesMemo, similarity);
        }
      }

      applyAction(action, answer, skipped)
        .then(() => {
          assistantQuery.state.setAssistantActions((current) => ([
            ...(current ?? []),
            ...[{
              actionId: action.actionId,
              answer: answer,
              applied: true
            }]
          ]));

          if (![constants.assistant.adviceActionTypes.askQuestion, constants.assistant.adviceActionTypes.needMoreInfo].includes(action.id)) {
            if (!skipped) {
              snackbar.show('Action was applied', null,
                {color: 'success', autoHideDuration: constants.delay.success});
            }

            if (utils.isDefined(activeMessage)) {
              const actions = (activeMessage.results?.actions ?? []).filter((act) => {
                return act.id !== constants.assistant.adviceActionTypes.needMoreInfo;
              });
              const applied = (activeMessage.results?.actions ?? []).filter((act) => {
                return (!skipped && act.actionId === action.actionId) ||
                  assistantQuery.state.assistantActions?.find((aa) => aa.applied && aa.actionId === act.actionId);
              });
              const appliedAll = (actions.length > 0 && actions.length === applied.length);

              if (skipped || appliedAll) {
                let messages = activeMessage.types.reduce((a, type) => {
                  const assistantType = constants.data.lookup('assistantTypes', type);
                  const adviceTypes = constants.data.assistantAdviceActions
                    .filter((at) => at.type === type)
                    .map((at) => at.value);

                  if (actions.find((a) => adviceTypes.includes(a.id))) {
                    const appliedLabel = applied
                      .filter((a) => adviceTypes.includes(a.id))
                      .map((a) => {
                        if (a?.id === constants.assistant.adviceActionTypes.updateQuery) {
                          return a?.description;
                        } else if ([
                          constants.assistant.adviceActionTypes.enableService,
                          constants.assistant.adviceActionTypes.disableService
                        ].includes(a?.id)) {
                          // see also ActionCard
                          const typeDef = constants.data.lookup('services', a?.details?.type);
                          return constants.assistant.adviceActionTypes.enableService === a?.id ?
                            `Enabled ${typeDef.label}` : `Disabled ${typeDef.label}`
                        } else {
                          return a?.details?.name ?? a?.details?.label ?? a?.name;
                        }
                      }).join(', ');

                    const label = !skipped ? appliedLabel : (
                      applied.filter((a) => adviceTypes.includes(a.id)).length > 0 ? appliedLabel : (
                        answer.toLowerCase() === 'skip' ? 'Skipped' : 'Skipped all'
                      )
                    );

                    a.push({
                      type: type,
                      question: assistantType.label,
                      options: [{
                        label: label,
                        tilt: applied.length > 0 ? (auto ? 'auto' : 'applied') : 'skip'
                      }],
                      answer: label
                    });
                  }

                  return a;
                }, []);

                doAddMessage(utils.clone(assistantQuery.state.assistantConversation ?? [], true), messages);
              }
            }
          } else {
            let messages;

            if (skipped) {
              messages = [{
                type: action.details[0].type ?? activeMessage.types[0],
                question: action.title ?? constants.data.lookup('assistantAdviceActions', action?.id).label,
                options: [{
                  label: answer,
                  tilt: 'skip'
                }],
                answer: answer
              }];
            } else {
              messages = action.details.reduce((a, question, idx) => {
                if (utils.isDefined(answer[idx]) || question.required === false) {
                  const answerText = !utils.isDefined(answer[idx]) ? '' :
                    question.multiselect ? answer[idx].map((a) => a.label) : (answer[idx]?.label ?? answer[idx]);

                  a.push({
                    type: question.type,
                    question: question.text,
                    options: question.options,
                    answer: answerText
                  });
                }

                return a;
              }, []);
            }

            const tilts = utils.uniqueArray(messages.reduce((a, m) => {
              utils.toArray(m.answer).forEach((answer) => {
                const tilt = m.options?.find((opt) => (opt?.label ?? opt).toLowerCase() === answer.toLowerCase())?.tilt;
                if (utils.isDefined(tilt)) {
                  a.push(tilt);
                }
              });

              return a;
            }, []));
            const skip = tilts.find((t) => t === 'skip');
            const type = tilts.find((t) => constants.data.lookup('assistantTypes', t));
            const verified = action.id === constants.assistant.adviceActionTypes.needMoreInfo;

            doAddMessage(utils.clone(assistantQuery.state.assistantConversation ?? [], true), messages, (skip ? null : (type ? [type] : activeMessage.types)), verified);
            setInternalState(utils.updater({blocked: false}, true));
          }
        })
        .catch(() => {
          snackbar.show('Applying action failed', null,
            {color: 'error', autoHideDuration: constants.delay.success});
        })
        .finally(() => {
          if (!skipped) {
            setApplying(false);
          }
        });
    }

    if (!skipped && ![constants.assistant.adviceActionTypes.askQuestion, constants.assistant.adviceActionTypes.needMoreInfo, constants.assistant.adviceActionTypes.updateQuery].includes(action.id)) {
      dialogControl.show(<ConfirmDialog question="Are you sure you want to apply this action?"
                                        explanation="The action will be applied and saved to the database"
                                        onConfirm={handleConfirm}/>, true, handleClose);
    } else {
      handleConfirm();
    }
  }

  const handlePage = (message) => {
    utils.retry(() => {
      const idx = conversationMemo.findIndex((m) => m.messageId === message.messageId);
      const ele = innerRef.current?.querySelector?.(`.AssistantQuery-conversation [data-list-index="${idx}"]`);
      if (ele) {
        dom.scrollIntoView(ele, {align: 'top'});
      }
    }, 3).then();
  }

  const renderConversationCard = (message, state) => {
    return <ConversationCard key={message.messageId}
                             message={message}
                             fullWidth={true}
                             autoApply={autoApply}
                             onApply={handleAction}
                             onPage={() => handlePage(message)}
                             selected={state.selected}/>
  }

  const isConversationItemEqual = (message, selected) => {
    return message.messageId === selected.messageId;
  }

  const handleChangeDirect = useCallback((e) => {
    setInternalState(utils.updater({
      sendDisabled: !(e.target.value?.length > 0)
    }, true));
  }, []);

  const handleSubmit = useCallback((values, actions) => {
    doAddMessage(utils.clone(assistantQuery.state.assistantConversation ?? [], true), values.message, activeMessage?.types);

    actions.resetForm();
    setInternalState(utils.updater({blocked: false, showMessage: false, sendDisabled: true}, true));
  }, [assistantQuery.state.assistantConversation, activeMessage?.types, doAddMessage]);

  const handleProcessMore = () => {
    if (assistantQuery.state.error) {
      setInternalState(utils.updater({blocked: false}, true));
    } else {
      const conversation = utils.clone(assistantQuery.state.assistantConversation ?? [], true);
      const activeMessage = (conversation ?? []).findLast((c) => c.role === constants.assistant.conversation.roles.user);

      activeMessage.progress.page += 1;
      activeMessage.progress.part = 0;

      setAssistantConversationEvent?.(conversation);
    }
  }

  const handleDeleteLast = () => {
    let conversation = utils.clone(assistantQuery.state.assistantConversation ?? [], true);

    const activeMessageIdx = conversation.findLastIndex((c) => c.role === constants.assistant.conversation.roles.user);
    conversation = conversation.slice(0, activeMessageIdx);

    const previousMessageIdx = conversation.findLastIndex((c) => c.role === constants.assistant.conversation.roles.user);
    if (previousMessageIdx !== -1) {
      const auto = conversation[previousMessageIdx].auto;
      const message = conversation[previousMessageIdx].message;
      const types = conversation[previousMessageIdx].types;
      const verified = conversation[previousMessageIdx].verified;
      const placeholder = conversation[previousMessageIdx].placeholder;
      conversation = conversation.slice(0, previousMessageIdx);

      const isAnswer = (message?.question || message?.[0]?.question);
      const tilts = isAnswer ? utils.uniqueArray(utils.toArray(message).reduce((a, m) => {
        utils.toArray(m.answer).forEach((answer) => {
          const tilt = m.options?.find((opt) => (opt?.label ?? opt).toLowerCase() === answer.toLowerCase())?.tilt;
          if (utils.isDefined(tilt)) {
            a.push(tilt);
          }
        });

        return a;
      }, [])) : [];
      const skip = tilts.find((t) => t === 'skip');
      const type = tilts.find((t) => constants.data.lookup('assistantTypes', t));

      doAddMessage(conversation, message, (skip || auto) ? null : (type ? [type] : types), (skip || auto) ? false : verified, placeholder, auto);
      setInternalState(utils.updater({blocked: false}, true));
    } else {
      setAssistantConversationEvent?.(conversation);
      setInternalState(utils.updater({blocked: false}, true));
    }
  }

  const renderConversation = () => {
    return <Box className="AssistantQuery-conversation">
      <ListWrapper>
        <DataList data={conversationMemo}
                  count={conversationMemo?.length}
                  dataKey="messageId"
                  renderItem={renderConversationCard}
                  isItemEqual={isConversationItemEqual}
                  emptyText="No messages found"
                  ListProps={{
                    track: false,
                    catchFocus: false,
                    virtualize: false,
                    gap: 24
                  }} />
      </ListWrapper>
    </Box>
  }

  const handleNavigationClick = (navigation) => {
    return () => {
      doAddMessage(utils.clone(assistantQuery.state.assistantConversation ?? [], true), navigation.label, navigation.types, navigation.types?.length > 0);
    }
  };

  const renderNavigation = () => {
    if (navigation) {
      if (assistantQuery.state.navigation?.length > 0) {
        return <Box className="AssistantQuery-conversation-navigation">
          <List track={true} catchFocus={false} orientation="horizontal" gap={4}>
            {assistantQuery.state.navigation
              .filter((navigation) => {
                return navigation !== constants.assistant.types.setup &&
                  constants.data.lookup('assistantNavigationTypes', navigation)
              })
              .map((navigation, idx) => {
                const navigationDef = constants.data.lookup('assistantNavigationTypes', navigation);
                return <ListItem key={idx}>
                  <Button variant="outlined"
                          plainBorder={true}
                          size="smaller"
                          onClick={handleNavigationClick(navigationDef)}>
                    {navigationDef.label}
                  </Button>
                </ListItem>
              })}
          </List>
        </Box>
      }
    }
  }

  const renderFooter = () => {
    const showDeleteLast = utils.isDefined(activeMessage) && !activeMessage.auto &&
      (assistantQuery.state.error || +(activeMessage.progress?.total ?? 0) >= 0);
    const showProcessMore = isSearchAssistant && utils.isDefined(activeMessage) &&
      +(activeMessage.progress?.total ?? 0) !== 100 && (assistantQuery.state.error || +(activeMessage.progress?.total ?? 0) > 0);
    const adviceNextStep = constants.data.lookup('assistantNavigationTypes', assistantQuery.state.navigation?.find((n) => n === constants.assistant.types.setup));

    return <Box className="AssistantQuery-footer">
      <Box className="AssistantQuery-actions">
        <Box className="AssistantQuery-actions-left">
          {actions.map((action, idx) => {
            return <ActionButton key={idx}
                                 variant="text"
                                 size="medium"
                                 action={action} />
          })}
        </Box>
        <Box className="AssistantQuery-actions-right">
          {!assistantQuery.state.showMessage ? (showProcessMore ?
            <Button children={assistantQuery.state.error ? (xsDown ? 'Retry' : 'Retry processing') : (lsDown ? `Process${xsDown ? '' : ' more'}` : 'Process more companies')}
                    variant="text"
                    startIcon={<Icon icon={AutoAwesome}/>}
                    onClick={handleProcessMore}/> : (adviceNextStep ?
              <Button children={lsDown ? `Advise${xsDown ? '' : ' step'}` : 'Advise next step'}
                      variant="text"
                      startIcon={<Icon icon={Help}/>}
                      onClick={handleNavigationClick(adviceNextStep)}/> : null)
          ) : null}
          {!assistantQuery.state.showMessage ? (showDeleteLast ?
            <Button children={lsDown ? `Remove${xsDown ? '' : ' last'}` : 'Remove last instruction'}
                    variant="text"
                    startIcon={<Icon icon={Delete}/>}
                    onClick={handleDeleteLast}/> : null) : null}
          {!assistantQuery.state.showMessage ?
            <Button children={lsDown ? 'Add' : 'Add instruction'}
                    variant="text"
                    startIcon={<Icon icon={Add}/>}
                    onClick={() => {
                      setInternalState(utils.updater({showMessage: true, sendDisabled: true}, true));
                    }}/> : null}
          {assistantQuery.state.showMessage ?
            <Button children={'Cancel'}
                    variant="text"
                    startIcon={<Icon icon={Close}/>}
                    onClick={() => {
                      setInternalState(utils.updater({showMessage: false, sendDisabled: true}, true));
                    }}/> : null}
          {assistantQuery.state.showMessage ?
            <Button type="submit"
                    variant="contained"
                    children={lsDown ? 'Add' : 'Add instruction'}
                    disabled={assistantQuery.state.sendDisabled}
                    startIcon={<Icon icon={Add}/>}
                    onClick={() => {
                      formRef.current?.submit();
                    }}/> : null}
        </Box>
      </Box>
    </Box>
  }

  useEffect(() => {
    if (hasAssistantQuery && assistantQuery.state.assistantConversation?.length > 0 && !initialisedRef.current) {
      initialisedRef.current = true;

      if (needsRecalculation) {
        let conversation = utils.clone(assistantQuery.state.assistantConversation ?? [], true);

        const activeMessageIdx = conversation.findLastIndex((c) => c.role === constants.assistant.conversation.roles.user);
        const auto = conversation[activeMessageIdx].auto;
        const message = conversation[activeMessageIdx].message;
        const types = conversation[activeMessageIdx].types;
        const verified = conversation[activeMessageIdx].verified;
        const placeholder = conversation[activeMessageIdx].placeholder;
        conversation = conversation.slice(0, activeMessageIdx);

        const isAnswer = (message?.question || message?.[0]?.question);
        const tilts = isAnswer ? utils.uniqueArray(utils.toArray(message).reduce((a, m) => {
          utils.toArray(m.answer).forEach((answer) => {
            const tilt = m.options?.find((opt) => (opt?.label ?? opt).toLowerCase() === answer.toLowerCase())?.tilt;
            if (utils.isDefined(tilt)) {
              a.push(tilt);
            }
          });

          return a;
        }, [])) : [];
        const skip = tilts.find((t) => t === 'skip');
        const type = tilts.find((t) => constants.data.lookup('assistantTypes', t));

        doAddMessage(conversation, message, (skip || auto) ? null : (type ? [type] : types), (skip || auto) ? false : verified, placeholder, auto);

        setTimeout(() => {
          setInternalState(utils.updater({initialised: true, blocked: false}, true));
        }, constants.debounce.minimal);
      } else {
        setInternalState(utils.updater({initialised: true, blocked: false}, true));
      }
    }
  }, [hasAssistantQuery, assistantQuery.state.assistantConversation, doAddMessage, needsRecalculation]);

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledAssistantQuery ref={innerRef} {...innerProps}>
    <Box className="AssistantQuery-content">
      {renderConversation()}
      {renderNavigation()}
    </Box>
    {assistantQuery.state.showMessage ?
      <InlineForm ref={formRef}
                  className="AssistantQuery-form"
                  onChangeDirect={handleChangeDirect}
                  onSubmit={handleSubmit}
                  fields={fields}/> : null}
    {renderFooter()}
  </StyledAssistantQuery>
});

AssistantQuery.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  terms: PropTypes.array,
  wizard: PropTypes.string,
  examples: PropTypes.array,
  similarity: PropTypes.string,
  query: PropTypes.object,
  state: PropTypes.object,
  count: PropTypes.number,
  topIds: PropTypes.array,
  fieldData: PropTypes.object,
  isLoading: PropTypes.bool,
  initialised: PropTypes.bool,
  actions: PropTypes.array,
  autoStart: PropTypes.bool,
  autoApply: PropTypes.bool,
  navigation: PropTypes.bool,
  onChange: PropTypes.func,
  onToggle: PropTypes.func
};

export default AssistantQuery;
